import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Button, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import Spinner from '~/components/Common/Spinner';
import { BundleFilter } from '~/components/Cruises/pages/Bundles/types';

import { useDeleteBundleFilter } from '~/hooks/apis/cruises/useBundleFilterMutations';
import { useGetBundleFilters } from '~/hooks/apis/cruises/useGetBundleFilters';

import BundlesFilterCreate from './BundlesFilterCreate';

type Props = {
  vendorId: string;
};

function BundlesFilterList({ vendorId }: Props) {
  const [deleteButton, setDeleteButton] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { bundleId, action } = useParams<{ bundleId: string; action?: string }>();

  const { data: bundleFilters = [], isLoading, error } = useGetBundleFilters(bundleId);
  const deleteMutation = useDeleteBundleFilter();

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync({ bundleId, filterId: id });
        enqueueSnackbar('Bundle filter deleted', { variant: 'success' });
        setDeleteButton(null);
      } catch (err) {
        enqueueSnackbar('Error deleting bundle filter', { variant: 'error' });
      }
    },
    [enqueueSnackbar, bundleId, deleteMutation],
  );

  const actionColumn = useCallback(
    (_cell: unknown, row: BundleFilter) => (
      <Box>
        {deleteButton !== row.id && (
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDeleteButton(row.id);
            }}
          >
            Delete
          </Button>
        )}

        {deleteButton === row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await handleDelete(row.id);
              }}
            >
              Confirm
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    ),
    [deleteButton, handleDelete],
  );

  const columns: Array<GridColDef> = [
    {
      field: 'rateId',
      headerName: 'Rate ID',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
      renderCell: (params) => (
        <Link to={`/cruises/rate-code-management/${params.value}/edit`} target="_blank" rel="noreferrer">
          {params.value}
        </Link>
      ),
    },
    {
      field: 'rateCode',
      headerName: 'Rate Code',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 180,
      sortable: false,
      renderCell: (params) => actionColumn(params.value, params.row),
      display: 'flex',
    },
  ];

  if (isLoading) {
    return <Spinner size={30} />;
  }

  if (error) {
    return <Alert severity="error">Error loading bundle filters: {error.message}</Alert>;
  }

  return (
    <Box mt={1}>
      <Box>
        <DataGrid
          rows={bundleFilters}
          columns={columns}
          getRowId={(row: BundleFilter) => row.id}
          autoHeight
          hideFooter
        />
      </Box>

      <Stack
        mt={2}
        width="100%"
        direction="row"
        justifyContent="flex-end"
        paddingLeft={2}
        paddingRight={2}
        paddingBottom={1}
      >
        <Button
          component={Link}
          to={`/cruises/bundles/edit/${bundleId}/filters/create`}
          variant="outlined"
          size="small"
          fullWidth
        >
          Add Bundle Filter
        </Button>
      </Stack>

      {action === 'create' && <BundlesFilterCreate vendorId={vendorId} />}
    </Box>
  );
}

export default BundlesFilterList;
